import React, { useEffect, useState } from 'react';
import CsvIcon from 'assets/images/csv.png';
import { ReactComponent as ListIcon } from 'assets/icons/list-icon.svg';
import AddMealForm from './add-meal-form';
import CsvInfoModal from 'components/modals/csv-info-modal';
import { MealFormType } from 'models/production-report.model';
import * as XLSX from 'xlsx';
import useProductionReportContext from 'contexts/kitchen/production-report.context/context';
import { useNavigate } from 'react-router-dom';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';

const mealAdditionDetails = {
  subtitle:
    'Welcome to the meal addition process via CSV file. Please Follow these steps to ensure a meal addition:',
  steps: [
    {
      number: 1,
      heading: 'Download the Template',
      description: "Click the 'Download CSV Template' button to obtain the template file."
    },
    {
      number: 2,
      heading: 'Fill in meal Details',
      description: ' Open the downloaded CSV file and fill up all the details needed.'
    },
    {
      number: 3,
      heading: 'Save Your Changes',
      description: 'Save the CSV file with your entries'
    },
    {
      number: 4,
      heading: 'Review',
      description:
        'Crosscheck the details in the CSV file. Click confirm to select the file and upload'
    },
    {
      number: 5,
      heading: 'Verify the details',
      description:
        'After uploading, the details will be displayed for final review before addition,then click continue to add meals'
    }
  ]
};

export const defaultMealData: MealFormType = {
  section: '',
  meal_category: '',
  meal_name: '',
  min_quantity: 0
};

const AddMeal = () => {
  const { mealFormData, setMealFormData, active, setActive, setIsBackLink, setBackLinkTitle } =
    useProductionReportContext();
  const [showCsvInfoModal, setShowCsvInfoModal] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to previous');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  const closeCsvInfoModal = () => {
    setShowCsvInfoModal(false);
    if (file) setActive('csv');
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files) setFile(files[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileUpload = (e: any) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const readFile = () => {
    if (file) {
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const mealDetails = workbook.SheetNames[0];
        const convertedMealDetails: MealFormType[] = XLSX.utils.sheet_to_json(
          workbook.Sheets[mealDetails]
        );

        const mealFormData = convertedMealDetails.map((meal) => {
          return meal;
        });

        setMealFormData(mealFormData);
      };
      if (mealFormData.length <= 1) {
        setMealFormData(mealFormData);
      } else {
        navigate(MAIN_STORE_ROUTE_KEYS.KITCHEN_REVIEW_MEAL);
      }
    }
  };

  useEffect(() => {
    closeCsvInfoModal();
    readFile();
  }, [file]);

  const reset = () => {
    if (active === 'csv') {
      setActive(null);
      setMealFormData([]);
    }
  };

  useEffect(() => {
    reset();
  }, []);

  return (
    <div>
      <div className='w-screen py-2 px-4 text-center md:py-0 bg-[#FFF5F6] min-h-[63px] flex justify-center items-center prompt -translate-x-4 mb-4 mt-[-24px]'>
        <div className='flex gap-4 justify-center md:justify-start md:gap-12 items-center flex-wrap'>
          <p className='text-red-5'>
            <span className=''> ⚠️ </span>
            To enable automated deductions, new kitchen item must be mapped to meals on the mobile
            app. Reach out to Brand dev (Siki) for assistance
          </p>
        </div>
      </div>

      <div className=' w-full flex justify-center'>
        <div className='flex flex-col'>
          <h2 className='text-2xl font-semibold my-4'>Add Kitchen Item</h2>

          <div className=' max-w-[400px] flex gap-4 my-4'>
            <div
              onClick={() => setShowCsvInfoModal(true)}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              className={`${
                active === 'csv' ? 'border-[#E60A2B] border-[1.5px]' : 'border-[#EAEBEB]'
              } border w-full sm:w-[185px] h-[139px]  hover:border-[#E60A2B] cursor-pointer hover:border-[1.5px] p-4 rounded-md space-y-3`}>
              <div className='border border-[#EAEBEB] p-2 rounded-full w-9 h-9 flex items-center'>
                <img src={CsvIcon} alt='' />
              </div>
              <h2 className='text-sm font-semibold'>Add via .csv</h2>
              <p className='text-[#6F7376] text-xs font-normal'>
                Add multiple items to the store list
              </p>
            </div>

            <div
              onClick={() => {
                setActive('manual');
                setFile(null);
                setMealFormData([defaultMealData]);
              }}
              className={`${
                active === 'manual' ? 'border-[#E60A2B] border-[1.5px]' : 'border-[#EAEBEB] '
              } border w-full sm:w-[185px] h-[139px] hover:border-[#E60A2B] cursor-pointer hover:border-[1.5px] p-4 rounded-md space-y-3`}>
              <div className='border border-[#EAEBEB] p-2 rounded-full w-9 h-9 flex items-center'>
                <ListIcon />
              </div>
              <h2 className='text-sm font-semibold'>Add Manually</h2>
              <p className='text-[#6F7376] text-xs font-normal '>
                Add multiple items to the store list
              </p>
            </div>
          </div>

          {showCsvInfoModal && (
            <CsvInfoModal
              show={showCsvInfoModal}
              onClose={closeCsvInfoModal}
              title='Add Meals via CSV file'
              downloadLink='https://docs.google.com/spreadsheets/d/1LX5C4MF2-pd3L_VtAk2cgOkEHlgz96T9bPvq5pvn0gA/export?format=xlsx'
              handleFileUpload={handleFileUpload}>
              <div className='w-full max-w-[400px]'>
                <h2 className='text-sm text-[#2E3031]'>{mealAdditionDetails.subtitle}</h2>
                <div>
                  {mealAdditionDetails.steps.map((step) => (
                    <div className='flex items-center max-w-[400px]' key={step.number}>
                      <div className='inline-block relative'>
                        <span className='w-5 h-5 bg-[#e7e8e9] text-sm rounded-full flex items-center justify-center mb-2 mr-2 p-1'>
                          {step.number}
                        </span>
                      </div>
                      <span className='mt-3'>
                        <h3 className='text-sm font-medium '>{step.heading}</h3>
                        <p className='text-xs'>{step.description}</p>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CsvInfoModal>
          )}
          {active && mealFormData.length ? <AddMealForm /> : null}
        </div>
      </div>
    </div>
  );
};

export default AddMeal;
